<template>
  <div v-if="!loading">
    <div class="product container">
      <ul class="images">
        <li
          v-for="(image, index) in images"
          :key="image.id"
          class="image__wrapper"
        >
          <WishlistButton
            v-if="index === 0"
            class="wishlist-button"
            :product-id="product.id"
          />
          <IntersectionObserver
            v-slot="{ intersected }"
            :options="{
              root: null,
              threshold: index === 1 ? 0.15 : 0,
              rootMargin: '0px 0px 0px 0px',
            }"
          >
            <Video
              v-if="image.mediaContentType === 'VIDEO'"
              :video="image"
              :intersected="intersected"
            />
            <Picture
              v-if="image.mediaContentType !== 'VIDEO'"
              :images="image"
              :intersected="intersected"
            />
          </IntersectionObserver>
        </li>
      </ul>

      <div class="content">
        <div class="product__header">
          <div v-if="isOnSale" class="product__onsale">SALE</div>
          <h1 id="hello" class="product__title">{{ product.title }}</h1>
          <p v-if="product.shortDescription" class="product__description">
            {{ product.shortDescription.value }}
          </p>
          <div v-if="hasMultipleVariants" class="product__help">
            <Button transparent @click="openSizeGuide = true"
              >Size guide</Button
            >
            <Button transparent easysize>Need help with your size?</Button>
            <div class="easysize_placeholder"></div>
          </div>

          <SizeGuide v-model:open="openSizeGuide" :sizes="sizeGuide" />

          <AddToBag
            :product="product"
            :selected-variant="selectedVariant"
            :default-variant="defaultVariant"
          />

          <DeliveryEstimatorNoInput
            v-if="isEDDEnabled && variant.availableForSale"
          />

          <div v-if="showBNPL" class="u__background--white product__payment">
            <BNPLInformation :price="variant?.priceV2" />
            <DeliveryInformation v-if="false" />
          </div>
          <div
            v-if="isEDDEnabled"
            class="u__background--white product__payment product__edd"
          >
            <DeliveryEstimator :weight="selectedVariantWeight" />
          </div>
        </div>
        <div class="u__background--white product__details">
          <Tabs class="product-details-tabs">
            <Tab name="details">
              <div class="wysiwyg" v-html="product.descriptionHtml"></div>
            </Tab>
            <Tab name="Sizing">
              <div class="wysiwyg">
                <template v-if="sizeInfo">
                  <p v-for="(paragraph, index) in sizeInfo" :key="index">
                    {{ paragraph }}
                  </p>
                </template>
                <p>
                  Find your perfect fit with the
                  <span class="text-link" @click="openSizeGuide = true"
                    >Size Guide</span
                  >
                </p>
              </div>
            </Tab>
            <Tab name="Shipping">
              <ShippingRates />
            </Tab>

            <Tab name="Returns">
              <div class="wysiwyg">
                <p>
                  You may return any items from your order within 30 days of
                  receipt using a returns label that we can provide or a courier
                  of your choice. With the exception of our Tier 3 and 4
                  members, customers are expected to cover the cost of all
                  returns.
                </p>
              </div>
            </Tab>
            <Tab v-if="product.careInfo" name="Fabric &amp; Care">
              <div class="wysiwyg">
                <p v-for="(paragraph, index) in careInstructions" :key="index">
                  {{ paragraph }}
                </p>
              </div>
            </Tab>
          </Tabs>
        </div>
      </div>
    </div>
    <InstagramFeed
      v-if="howToWearTiles.length > 1"
      heading="How you're wearing it"
      subheading="Spotlight on @ratandboa"
      :tiles="howToWearTiles"
    />

    <VideoCarousel
      v-if="instagramReels.length > 1"
      heading="Instagram Videos"
      :videos="instagramReels"
    />

    <div class="js-nosto-placement">
      <NostoProductRecommendations
        v-if="nostoProductRecommendationsProduct"
        :recommendations="nostoProductRecommendationsProduct"
        :placement="nostoProductRecommendationsProductId"
        heading="You might also like"
      />
      <NostoProductRecommendations
        v-if="nostoProductRecommendationsBehaviour"
        :recommendations="nostoProductRecommendationsBehaviour"
        :placement="nostoProductRecommendationsBehaviourId"
        heading="An edit just for you"
      />
    </div>
    <div class="js-shopify-placement">
      <ProductRecommendations :product-id="product.id" />
    </div>

    <Newsletter />
  </div>
  <ProductSkeleton v-else />
</template>

<script>
import cloneDeep from "lodash-es/cloneDeep";
import { mapActions, mapGetters, mapState } from "vuex";

import Button from "@/components/Button.vue";
import InstagramFeed from "@/components/InstagramFeed.vue";
import NostoProductRecommendations from "@/components/NostoProductRecommendations.vue";
import Picture from "@/components/Picture.vue";
import AddToBag from "@/components/product-display/AddToBag.vue";
import BNPLInformation from "@/components/product-display/BNPLInformation.vue";
import DeliveryEstimator from "@/components/product-display/DeliveryEstimator.vue";
import DeliveryEstimatorNoInput from "@/components/product-display/DeliveryEstimatorNoInput.vue";
import ShippingRates from "@/components/product-display/ShippingRates.vue";
import DeliveryInformation from "@/components/product-display/DeliveryInformation.vue";
import ProductRecommendations from "@/components/product-display/ProductRecommendations.vue";
import SizeGuide from "@/components/product-display/SizeGuide.vue";
import ProductSkeleton from "@/components/skeleton/ProductSkeleton.vue";
import Tab from "@/components/Tab";
import Tabs from "@/components/Tabs";
import Video from "@/components/Video.vue";
import VideoCarousel from "@/components/VideoCarousel.vue";
import WishlistButton from "@/components/WishlistButton.vue";
import { regionFeatureToggles } from "@/config.js";
import productQuery from "@/graphql/queries/Product.gql";
import { viewProductEvent } from "@/helpers/events.js";
import { unwrapNodes } from "@/helpers/graphql";
import { capitalizeFirstLetter } from "@/helpers/string-manipulation.js";
import { getParams } from "@/helpers/url.js";
import IntersectionObserver from "@/components/IntersectionObserver.vue";
import Newsletter from "@/components/Newsletter.vue";

export default {
  name: "ProductPage",
  components: {
    AddToBag,
    BNPLInformation,
    DeliveryInformation,
    InstagramFeed,
    ProductRecommendations,
    Tabs,
    Tab,
    Newsletter,
    Picture,
    Video,
    NostoProductRecommendations,
    Button,
    SizeGuide,
    IntersectionObserver,
    ProductSkeleton,
    DeliveryEstimator,
    DeliveryEstimatorNoInput,
    VideoCarousel,
    ShippingRates,
    WishlistButton,
  },
  props: {
    productHandle: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      product: null,
      selectedVariant: null,
      defaultVariant: null,
      variantBySelectedOptions: null,
      openSizeGuide: false,
      productId: null,
      nostoProductRecommendationsProduct: null,
      nostoProductRecommendationsProductId: null,
      nostoProductRecommendationsBehaviour: null,
      nostoProductRecommendationsBehaviourId: null,
      referredPlacement: null,
      loading: true,
    };
  },
  computed: {
    ...mapState("product", ["selectedOptions"]),
    ...mapGetters("product", ["selectedOptionsWithoutId"]),
    ...mapGetters("app", ["currencyCode"]),
    variant() {
      return this.selectedVariant || this.defaultVariant;
    },
    hasMultipleVariants() {
      const options = this.product.options;
      return options.some((option) => option.values.length > 1);
    },
    images() {
      const medias = this.product.media
        ? unwrapNodes(this.product.media.edges)
        : [];
      const video = medias.filter(
        (media) => media.mediaContentType === "VIDEO"
      )[0];

      const allImages = this.product.images
        ? unwrapNodes(this.product.images?.edges)
        : [];
      const imagesWithoutFacebook = allImages.filter(
        (image) => image.altText !== "fb-feed"
      );

      if (video) {
        imagesWithoutFacebook.splice(1, 0, video);
      }

      return imagesWithoutFacebook;
    },
    showBNPL() {
      if (this.variant) {
        return true;
      }

      return false;
    },
    sizeGuide() {
      const sizes = [];
      const productVariants = unwrapNodes(this.product.variants?.edges) || [];
      productVariants.forEach((variant) => {
        const metafields = cloneDeep(variant.sizes);

        const notNullMetafields = metafields.filter(
          (metafield) =>
            !(typeof metafield === "undefined" || metafield === null)
        );

        const metafieldsSorted = notNullMetafields.sort((a, b) =>
          a.key > b.key ? 1 : -1
        );

        const title = variant.title.split(" / ")[0];

        const size = {
          title,
          measurements: metafieldsSorted,
        };
        sizes.push(size);
      });

      return sizes;
    },

    instagramTiles() {
      const metafields = unwrapNodes(this.product.metafields?.edges) || [];

      const instagramMetafields = metafields
        .filter((metafield) => metafield.key.includes("instagram"))
        .filter((metafield) => !!metafield?.reference?.image?.largeMedia);

      return instagramMetafields.map((ig) => {
        return {
          imageUrl: ig.reference.image.largeMedia,
        };
      });
    },

    howToWearTiles() {
      const metafields =
        unwrapNodes(this.product.howtowear?.references?.edges) || [];

      return metafields.map((mf) => {
        return {
          imageUrl: mf.image.largeMedia,
        };
      });
    },

    instagramReels() {
      return unwrapNodes(this.product.instagramReels?.references?.edges) || [];
    },

    careInstructions() {
      const str = this.product.careInfo.value;
      const paragraphs = str.split(/\r?\n/);
      return paragraphs;
    },
    sizeInfo() {
      const str = this.product.sizeInfo?.value;
      if (!str) {
        return false;
      }

      const paragraphs = str.split(/\r?\n/);
      return paragraphs;
    },
    selectedVariantWeight() {
      return this.selectedVariant?.weight || 0.2;
    },
    isEDDEnabled() {
      if (this.currencyCode) {
        return regionFeatureToggles[this.currencyCode].eddEnabled || false;
      }
      return false;
    },
    price() {
      return this.variant?.priceV2;
    },
    originalPrice() {
      return this.product.compareAtPriceRange.minVariantPrice;
    },
    isOnSale() {
      return (
        parseFloat(this.originalPrice.amount) > parseFloat(this.price.amount)
      );
    },
  },
  watch: {
    productId(val) {
      // eslint-disable-next-line no-undef
      nostojs((api) => {
        api
          .defaultSession()
          .viewProduct(val)
          .setRef(this.productId, this.referredPlacement)
          .setPlacements(["productpage-nosto-1", "productpage-nosto-2"])
          .load()
          .then((data) => {
            this.nostoProductRecommendationsProduct =
              data.recommendations["productpage-nosto-1"]?.products || null;
            this.nostoProductRecommendationsProductId =
              data.recommendations["productpage-nosto-1"]?.result_id || null;
            this.nostoProductRecommendationsBehaviour =
              data.recommendations["productpage-nosto-2"]?.products || null;
            this.nostoProductRecommendationsBehaviourId =
              data.recommendations["productpage-nosto-2"]?.result_id || null;
          });
      });

      viewProductEvent(this.product);

      this.setRecentlyViewedProducts(this.product.id);
    },
  },
  created() {
    this.params = getParams(window.location);
    this.referredPlacement = this.params["ref-placement"];
  },
  methods: {
    ...mapActions("product", ["setSelectedOptions", "setProduct"]),
    ...mapActions("app", ["setRecentlyViewedProducts"]),
  },
  apollo: {
    product: {
      query: productQuery,
      skip() {
        return !this.productHandle;
      },
      variables() {
        return {
          product: this.productHandle,
          selectedOptions: this.selectedOptionsWithoutId || [],
        };
      },
      result({ data }) {
        this.loading = false;

        if (!data) {
          return;
        }

        this.productId = data?.product.id.split("Product/")[1];

        this.setProduct(data?.product);

        if (this.selectedOptions.length === 0) {
          const options = data?.product.options;
          const optionsTitles = options.map((option) =>
            option.name.toLowerCase()
          );

          const actualOptions = Object.keys(this.params).filter((param) =>
            optionsTitles.includes(param)
          );

          const selectedOptions = [];
          actualOptions.forEach((name) => {
            const value = this.params[name];

            const { id } = options.find(
              (option) => option.name.toLowerCase() === name
            );

            selectedOptions.push({
              id,
              name: capitalizeFirstLetter(name),
              value: value.toUpperCase(),
            });
          });

          this.setSelectedOptions(selectedOptions);
        }

        const variants = unwrapNodes(data?.product.variants?.edges);

        const variantByOptions = data?.product.variantBySelectedOptions;
        if (variantByOptions) {
          this.selectedVariant = data?.product.variantBySelectedOptions;
        } else {
          const firstAvailableForSaleVariant = variants.find(
            (variant) => variant.availableForSale
          );

          if (firstAvailableForSaleVariant) {
            this.defaultVariant = firstAvailableForSaleVariant;
            return;
          }

          this.selectedVariant = variants[0];
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.product {
  @include respond-above(md) {
    display: grid;
    grid-template-columns: 3fr 2fr;
  }
  &__header {
    background-color: $color-light;
    padding: 30px 20px 30px;

    @include respond-above(md) {
      padding: 80px;
    }
  }

  &__onsale {
    color: #070707;
    font-size: 13px;
    margin: 0 0 5px 0;
    opacity: 0.3;
  }

  &__title {
    text-transform: uppercase;
    margin-bottom: 10px;
    font-size: 18px;
    font-weight: 700;
  }

  &__help {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin: 20px 0 10px 0;

    ::v-deep(.button) {
      font-size: 10px;
      font-weight: 400;
      letter-spacing: 0.55px;
      line-height: normal;
      padding: 0;
      text-transform: none;
    }

    ::v-deep(.easysize_button) {
      margin: 0;
      text-align: right;
      &:before {
        display: none;
      }
    }

    ::v-deep(.button:first-child) {
      text-align: left;
    }
  }

  &__description {
    font-size: 14px;
    font-family: $heading-font-family;
    margin-bottom: 20px;
  }

  &__details {
    padding: 30px 20px;

    @include respond-above(md) {
      padding: 40px 80px 80px;
    }
  }

  &__payment {
    margin-top: 10px;
    font-size: 11px;
    padding: 20px;

    &:first-child {
      margin-top: 20px;
    }
  }
}

.product.container {
  @include respond-below(md) {
    padding-left: 0;
    padding-right: 0;
  }
}

.content {
  @include respond-above(md) {
    align-self: start;
  }
}

.images {
  line-height: 0;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  display: flex;
  align-items: stretch;

  &::-webkit-scrollbar {
    display: none;
  }

  @include respond-above(md) {
    overflow: visible;
    white-space: normal;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2px;
  }

  .image__wrapper {
    display: inline-block;
    width: 87.5%;
    margin-right: 2px;
    flex: 1 0 87.5%;
    position: relative;

    @include respond-above(md) {
      display: block;
      width: 100%;
      margin-right: 0;
    }

    &:nth-child(3n + 1) {
      grid-column: 1 / span 2;
    }
  }

  .image__element {
    width: 100%;
    height: auto;
    display: block;
  }
}

::v-deep(.intersection-observer) {
  height: 100%;
}

.wysiwyg ::v-deep() {
  font-size: 11px;

  p {
    margin-bottom: 15px;
  }

  ul,
  ol {
    list-style: initial;
    margin-bottom: 15px;

    li {
      margin-left: 15px;
    }
  }

  *:last-child {
    margin-bottom: 0;
  }
}

.text-link {
  cursor: pointer;
  font-weight: 600;
}

::v-deep(.instagram-feed) {
  background-color: $color-light;
}

.product-details-tabs {
  ::v-deep(.tabs-component-tabs) {
    margin: 0 -20px;
    padding: 0 0 0 20px;
  }
}

.js-shopify-placement {
  display: none;
}

.wishlist-button {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 3;
  padding: 14px;
}
</style>
